<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import ReportsAPI from 'dashboard/api/reports';
import { useAlert } from 'dashboard/composables';
import ReportHeader from './components/ReportHeader.vue';
import ReportFilters from './components/ReportFilters.vue';
import BarChart from 'shared/components/charts/BarChart.vue';

const { t } = useI18n();

// ---------------------------------------------------------------------------
// This page merges the old Workforce Overview and Agent Performance reports
// into one curated view: KPI band, volume split, per-agent cards, funnel and
// attention signals. One fetch powers everything - the workforce payload's
// human.agents is the same per-agent dataset the old agent report consumed.
//
// Period: the standard report date-range filter (presets + custom range),
// which also syncs ?from&to&range URL params like the other report pages.
// ---------------------------------------------------------------------------
const from = ref(0);
const to = ref(0);

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------
const isFetching = ref(false);
// Bumped on every fetch; a response only commits if it's still the latest
// request in flight, so a slow stale response can't overwrite a newer one.
let requestId = 0;
const overview = ref(null);

const fetchData = async () => {
  requestId += 1;
  const thisRequestId = requestId;
  isFetching.value = true;
  try {
    const { data } = await ReportsAPI.getWorkforceOverview({
      from: from.value,
      to: to.value,
    });
    if (thisRequestId !== requestId) return;
    overview.value = data;
  } catch {
    if (thisRequestId !== requestId) return;
    useAlert(t('WORKFORCE_OVERVIEW_REPORTS.FETCH_ERROR'));
  } finally {
    if (thisRequestId === requestId) isFetching.value = false;
  }
};

// ReportFilters emits once on mount (restoring ?from&to&range URL params or
// defaulting to last 7 days), so this also drives the initial fetch.
const onFilterChange = range => {
  from.value = range.from;
  to.value = range.to;
  fetchData();
};

const volumeSplit = computed(() => overview.value?.volume_split || {});
const ai = computed(() => overview.value?.ai || {});
const human = computed(() => overview.value?.human || {});
const labels = computed(() => overview.value?.labels || {});
const escalations = computed(() => overview.value?.escalations || {});
const funnel = computed(() => overview.value?.funnel || {});
const agents = computed(() => human.value.agents || []);
const hasNoData = computed(
  () => !isFetching.value && !volumeSplit.value.total_active_conversations
);

const formatPercent = value => (value || value === 0 ? `${value}%` : '—');

// Compact two-unit durations (51d 20h, 11h 23m, 38s) - @chatwoot/utils'
// formatTime spells units out (51 Day 20 Hr), which wraps in tight card rows.
const formatCompactDuration = seconds => {
  if (!seconds && seconds !== 0) return '—';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  if (days) return hours ? `${days}d ${hours}h` : `${days}d`;
  if (hours) return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
  if (minutes) return secs ? `${minutes}m ${secs}s` : `${minutes}m`;
  return `${secs}s`;
};

// ---------------------------------------------------------------------------
// Colors: the validated categorical palette both old reports shared (checked
// with the dataviz skill's validator for adjacent-pair CVD separation and
// contrast). Fixed hue -> identity mapping: blue = AI-only, violet = AI+human
// (handoff), ruby = human-only. Agents beyond the 4th fall back to neutral -
// identity still comes through via the card that carries the color.
// ---------------------------------------------------------------------------
const BLUE = '#2781F6';
const VIOLET = '#6E56CF';
const RUBY = '#E54666';
const TEAL = '#12A594';
const CATEGORICAL_COLORS = [BLUE, RUBY, VIOLET, TEAL];
const neutralColor = ref('#8B8D98');

onMounted(() => {
  const resolved = getComputedStyle(document.documentElement)
    .getPropertyValue('--slate-9')
    .trim();
  if (resolved) neutralColor.value = `rgb(${resolved})`;
});

// Fixed by agent id so an agent's color never changes across periods or
// refetches.
const agentColorMap = computed(() => {
  const stableOrder = [...agents.value].sort((a, b) => a.id - b.id);
  return new Map(
    stableOrder.map((agent, index) => [
      agent.id,
      index < CATEGORICAL_COLORS.length
        ? CATEGORICAL_COLORS[index]
        : neutralColor.value,
    ])
  );
});

// Mark spec: thin bars (<=24px), 4px rounded data-end, square at the baseline
// (borderSkipped start), recessive hairline grid on the value axis only.
const BAR_SPEC = {
  borderRadius: 4,
  borderSkipped: 'start',
  maxBarThickness: 24,
};

const horizontalChartOptions = {
  indexAxis: 'y',
  scales: {
    x: {
      beginAtZero: true,
      ticks: { precision: 0 },
      border: { display: false },
    },
    y: {
      ticks: { autoSkip: false },
      grid: { display: false },
      border: { display: false },
    },
  },
  plugins: { legend: { display: false } },
};

// ---------------------------------------------------------------------------
// Trends: { label: '+12%', chipClass } - direction 'high' means higher is
// better (volume), 'low' means lower is better (response/resolution time).
// ---------------------------------------------------------------------------
const trendChip = (delta, direction = 'high') => {
  if (!delta) return null;
  const isGood = direction === 'high' ? delta > 0 : delta < 0;
  return {
    label: `${delta > 0 ? '+' : ''}${Math.round(delta)}%`,
    chipClass: isGood
      ? 'text-n-teal-11 bg-n-teal-3'
      : 'text-n-ruby-11 bg-n-ruby-3',
    textClass: isGood ? 'text-n-teal-11' : 'text-n-ruby-11',
  };
};

const pairTrend = (current, previous, direction = 'high') => {
  if (!previous) return null;
  const delta = Math.round(((current - previous) / previous) * 1000) / 10;
  return trendChip(delta, direction);
};

const packedTrend = (packed, direction = 'high') =>
  trendChip(packed?.trend, direction);

const aiTrend = computed(() =>
  pairTrend(
    ai.value.conversations_handled,
    ai.value.conversations_handled_previous
  )
);
const humanTrend = computed(() =>
  pairTrend(
    human.value.conversations_handled,
    human.value.conversations_handled_previous
  )
);

// ---------------------------------------------------------------------------
// Volume split chart + aligned stat rows sharing the bar colors
// ---------------------------------------------------------------------------
const volumeSplitChartData = computed(() => ({
  labels: [
    t('WORKFORCE_OVERVIEW_REPORTS.VOLUME_SPLIT.AI_RESOLVED'),
    t('WORKFORCE_OVERVIEW_REPORTS.VOLUME_SPLIT.AI_HANDED_OFF'),
    t('WORKFORCE_OVERVIEW_REPORTS.VOLUME_SPLIT.HUMAN_ONLY'),
  ],
  datasets: [
    {
      data: [
        volumeSplit.value.ai_resolved_no_human ?? 0,
        volumeSplit.value.ai_handed_off_to_human ?? 0,
        volumeSplit.value.never_touched_by_ai ?? 0,
      ],
      backgroundColor: [BLUE, VIOLET, RUBY],
      ...BAR_SPEC,
    },
  ],
}));

const volumeRows = computed(() => [
  {
    key: 'ai_touched',
    label: t('WORKFORCE_OVERVIEW_REPORTS.VOLUME_SPLIT.AI_TOUCHED'),
    value: volumeSplit.value.ai_touched ?? 0,
    dot: null,
  },
  {
    key: 'ai_resolved',
    label: t('WORKFORCE_OVERVIEW_REPORTS.VOLUME_SPLIT.AI_RESOLVED'),
    value: volumeSplit.value.ai_resolved_no_human ?? 0,
    dot: BLUE,
  },
  {
    key: 'handed_off',
    label: t('WORKFORCE_OVERVIEW_REPORTS.VOLUME_SPLIT.AI_HANDED_OFF'),
    value: volumeSplit.value.ai_handed_off_to_human ?? 0,
    dot: VIOLET,
  },
  {
    key: 'human_only',
    label: t('WORKFORCE_OVERVIEW_REPORTS.VOLUME_SPLIT.HUMAN_ONLY'),
    value: volumeSplit.value.never_touched_by_ai ?? 0,
    dot: RUBY,
  },
]);

const FUNNEL_COLORS = [BLUE, VIOLET, RUBY, TEAL];
const funnelChartData = computed(() => {
  const rows = [
    [
      t('WORKFORCE_OVERVIEW_REPORTS.FUNNEL.BUY_FOR_ME'),
      funnel.value.by_service?.buy_for_me ?? 0,
    ],
    [
      t('WORKFORCE_OVERVIEW_REPORTS.FUNNEL.SHIP_FOR_ME'),
      funnel.value.by_service?.ship_for_me ?? 0,
    ],
    [
      t('WORKFORCE_OVERVIEW_REPORTS.FUNNEL.BD_EXPORT'),
      funnel.value.by_service?.bd_export ?? 0,
    ],
    [
      t('WORKFORCE_OVERVIEW_REPORTS.FUNNEL.SHIP_ANYWHERE'),
      funnel.value.by_service?.ship_anywhere ?? 0,
    ],
    [
      t('WORKFORCE_OVERVIEW_REPORTS.FUNNEL.EID_SPECIAL'),
      funnel.value.by_service?.eid_special ?? 0,
    ],
  ].sort((a, b) => b[1] - a[1]);

  return {
    labels: rows.map(([label]) => label),
    datasets: [
      {
        data: rows.map(([, value]) => value),
        // 5th+ series falls back to neutral rather than a generated hue - each
        // bar already carries its identity via the axis label.
        backgroundColor: rows.map(
          (_, i) => FUNNEL_COLORS[i] ?? neutralColor.value
        ),
        ...BAR_SPEC,
      },
    ],
  };
});

const funnelStats = computed(() => [
  {
    key: 'quotes',
    label: t('WORKFORCE_OVERVIEW_REPORTS.FUNNEL.QUOTES_GIVEN'),
    value: String(funnel.value.quotes_given ?? 0),
  },
  {
    key: 'booked',
    label: t('WORKFORCE_OVERVIEW_REPORTS.FUNNEL.READY_TO_BOOK'),
    value: String(funnel.value.ready_to_book ?? 0),
  },
  {
    key: 'conversion',
    label: t('WORKFORCE_OVERVIEW_REPORTS.FUNNEL.CONVERSION_RATE'),
    value: formatPercent(funnel.value.conversion_rate),
  },
]);

const attentionStats = computed(() => [
  {
    key: 'customer-request',
    label: t('WORKFORCE_OVERVIEW_REPORTS.ESCALATIONS.CUSTOMER_REQUEST'),
    value: escalations.value['customer-request'] ?? 0,
  },
  {
    key: 'urgent-request',
    label: t('WORKFORCE_OVERVIEW_REPORTS.ESCALATIONS.URGENT_REQUEST'),
    value: escalations.value['urgent-request'] ?? 0,
  },
  {
    key: 'call-request',
    label: t('WORKFORCE_OVERVIEW_REPORTS.ESCALATIONS.CALL_REQUEST'),
    value: escalations.value['call-request'] ?? 0,
  },
  {
    key: 'high-value-lead',
    label: t('WORKFORCE_OVERVIEW_REPORTS.RISK.HIGH_VALUE_LEAD'),
    value: labels.value['high-value-lead'] ?? 0,
  },
  {
    key: 'at-risk',
    label: t('WORKFORCE_OVERVIEW_REPORTS.RISK.AT_RISK'),
    value: labels.value['at-risk'] ?? 0,
  },
  {
    key: 'blocked-item-inquiry',
    label: t('WORKFORCE_OVERVIEW_REPORTS.RISK.BLOCKED_ITEM_INQUIRY'),
    value: labels.value['blocked-item-inquiry'] ?? 0,
  },
]);

// ---------------------------------------------------------------------------
// Agent cards
// ---------------------------------------------------------------------------
const volumeMetrics = computed(() => [
  {
    key: 'conversations_handled',
    label: t('TEAM_PERFORMANCE_REPORTS.AGENT_CARDS.CONVERSATIONS'),
    direction: 'high',
    format: value => value ?? 0,
  },
  {
    key: 'messages_sent',
    label: t('TEAM_PERFORMANCE_REPORTS.AGENT_CARDS.MESSAGES'),
    direction: 'high',
    format: value => value ?? 0,
  },
  {
    key: 'calls_handled',
    label: t('TEAM_PERFORMANCE_REPORTS.AGENT_CARDS.CALLS'),
    direction: 'high',
    format: value => value ?? 0,
  },
]);

// A percent change against a tiny base is noise, not signal (+1000% just
// means the previous period had one conversation) - volume trends only show
// once the previous period had a meaningful floor of activity.
const TREND_MIN_BASE = 5;
const volumeTrend = (agent, metric) => {
  const packed = agent[metric.key];
  if ((packed?.previous ?? 0) < TREND_MIN_BASE) return null;
  return packedTrend(packed, metric.direction);
};

const resolvedTrend = agent =>
  volumeTrend(agent, { key: 'conversations_resolved', direction: 'high' });

const formatCsatRating = rating => (rating ? rating.toFixed(1) : '—');

// Team-level human outcomes for the section header: total resolves and the
// response-weighted CSAT average across agents.
const teamResolvedTotal = computed(() =>
  agents.value.reduce(
    (sum, agent) => sum + (agent.conversations_resolved?.current || 0),
    0
  )
);

const teamCsat = computed(() => {
  let weighted = 0;
  let responses = 0;
  agents.value.forEach(agent => {
    const count = agent.csat_responses || 0;
    if (count && agent.csat_rating?.current) {
      weighted += agent.csat_rating.current * count;
      responses += count;
    }
  });
  return responses ? (weighted / responses).toFixed(1) : null;
});

const timeMetrics = computed(() => [
  {
    key: 'avg_first_response_time',
    label: t('AGENT_PERFORMANCE_REPORTS.TABLE.AVG_FIRST_RESPONSE'),
    direction: 'low',
    format: formatCompactDuration,
  },
  {
    key: 'avg_resolution_time',
    label: t('AGENT_PERFORMANCE_REPORTS.TABLE.AVG_RESOLUTION_TIME'),
    direction: 'low',
    format: formatCompactDuration,
  },
]);

// Outcome metrics live in the card's row list; they join the leader
// competition but need no label/format defs of their own.
const OUTCOME_METRICS = [
  { key: 'conversations_resolved', direction: 'high' },
  { key: 'csat_rating', direction: 'high' },
];

const allMetrics = computed(() => [
  ...volumeMetrics.value,
  ...timeMetrics.value,
  ...OUTCOME_METRICS,
]);

// Leading agent id per metric - "low" metrics (response/resolution time) only
// compete among agents who actually handled a conversation, so an idle agent
// never reads as "fastest".
const leaders = computed(() => {
  const result = {};
  allMetrics.value.forEach(metric => {
    let bestId = null;
    let bestValue = null;
    agents.value.forEach(agent => {
      if (metric.direction === 'low' && !agent.conversations_handled?.current) {
        return;
      }
      const value = agent[metric.key]?.current;
      if (!value) return;
      const isBetter =
        bestValue === null ||
        (metric.direction === 'high' ? value > bestValue : value < bestValue);
      if (isBetter) {
        bestValue = value;
        bestId = agent.id;
      }
    });
    result[metric.key] = bestId;
  });
  return result;
});

const hasAgentActivity = agent =>
  agent.messages_sent?.current ||
  agent.conversations_handled?.current ||
  agent.calls_handled?.current;

// Cards ranked by conversations handled - performance order, busiest first,
// idle agents sinking to the end.
const rankedAgents = computed(() =>
  [...agents.value].sort(
    (a, b) =>
      (b.conversations_handled?.current || 0) -
      (a.conversations_handled?.current || 0)
  )
);

const totalTeamConversations = computed(() =>
  agents.value.reduce(
    (sum, agent) => sum + (agent.conversations_handled?.current || 0),
    0
  )
);

const agentShare = agent => {
  if (!totalTeamConversations.value) return 0;
  return Math.round(
    ((agent.conversations_handled?.current || 0) /
      totalTeamConversations.value) *
      100
  );
};

const agentInitials = name =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0])
    .join('')
    .toUpperCase();

// Meter spec: the unfilled track is a lighter step of the fill's own color
// (hex + 26 = ~15% alpha), so the whole bar reads as one agent's.
const shareTrackColor = agent => `${agentColorMap.value.get(agent.id)}26`;
</script>

<template>
  <ReportHeader :header-title="t('TEAM_PERFORMANCE_REPORTS.HEADER')" />
  <div class="flex flex-col gap-8 pb-12">
    <!-- Toolbar: standard report date-range filter (syncs ?from&to&range) -->
    <ReportFilters
      :show-entity-filter="false"
      :show-group-by="false"
      :show-business-hours="false"
      @filter-change="onFilterChange"
    />

    <woot-loading-state
      v-if="isFetching"
      :message="t('WORKFORCE_OVERVIEW_REPORTS.LOADING')"
    />
    <div
      v-else-if="hasNoData"
      class="flex flex-col items-center justify-center gap-2 py-24 text-center border border-dashed rounded-2xl border-n-weak"
    >
      <span class="i-lucide-bar-chart-3 size-6 text-n-slate-9" />
      <p class="text-sm text-n-slate-11">
        {{ t('WORKFORCE_OVERVIEW_REPORTS.EMPTY') }}
      </p>
    </div>

    <template v-else>
      <!-- KPI band: one hero + three supporting figures in a single card -->
      <section
        class="grid grid-cols-2 border divide-y lg:grid-cols-4 lg:divide-y-0 lg:divide-x divide-n-weak rounded-2xl border-n-weak bg-n-solid-1"
      >
        <div
          class="flex flex-col justify-between col-span-2 gap-6 p-6 lg:col-span-1"
        >
          <span class="text-sm font-medium text-n-slate-11">
            {{ t('WORKFORCE_OVERVIEW_REPORTS.VOLUME_SPLIT.TOTAL') }}
          </span>
          <div>
            <div class="text-5xl font-semibold tracking-tight text-n-slate-12">
              {{ volumeSplit.total_active_conversations ?? 0 }}
            </div>
            <div class="mt-2 text-xs text-n-slate-10">
              {{
                t('WORKFORCE_OVERVIEW_REPORTS.HERO.TOTAL_SUBTEXT', {
                  count: volumeSplit.ai_touched ?? 0,
                })
              }}
            </div>
          </div>
        </div>
        <div class="flex flex-col justify-between gap-6 p-6">
          <span class="text-sm font-medium text-n-slate-11">
            {{ t('WORKFORCE_OVERVIEW_REPORTS.HERO.AI_CONTAINMENT') }}
          </span>
          <div>
            <div class="text-3xl font-semibold text-n-slate-12">
              {{ formatPercent(volumeSplit.ai_containment_rate ?? 0) }}
            </div>
            <div class="mt-2 text-xs text-n-slate-10">
              {{ t('WORKFORCE_OVERVIEW_REPORTS.HERO.AI_CONTAINMENT_SUBTEXT') }}
            </div>
          </div>
        </div>
        <div class="flex flex-col justify-between gap-6 p-6">
          <span class="text-sm font-medium text-n-slate-11">
            {{ t('WORKFORCE_OVERVIEW_REPORTS.HERO.CONVERSION_RATE') }}
          </span>
          <div>
            <div class="text-3xl font-semibold text-n-slate-12">
              {{ formatPercent(funnel.conversion_rate) }}
            </div>
            <div class="mt-2 text-xs text-n-slate-10">
              {{
                t('WORKFORCE_OVERVIEW_REPORTS.HERO.CONVERSION_SUBTEXT', {
                  booked: funnel.ready_to_book ?? 0,
                  quoted: funnel.quotes_given ?? 0,
                })
              }}
            </div>
          </div>
        </div>
        <div class="flex flex-col justify-between gap-6 p-6">
          <span class="text-sm font-medium text-n-slate-11">
            {{ t('WORKFORCE_OVERVIEW_REPORTS.AI_VS_HUMAN.AI_HOURS_SAVED') }}
          </span>
          <div>
            <div class="text-3xl font-semibold text-n-slate-12">
              {{ ai.hours_saved ?? 0 }}
            </div>
            <div class="mt-2 text-xs text-n-slate-10">
              {{ t('WORKFORCE_OVERVIEW_REPORTS.HERO.HOURS_SAVED_SUBTEXT') }}
            </div>
          </div>
        </div>
      </section>

      <!-- Volume split: chart + aligned stat rows in one card -->
      <section class="flex flex-col gap-3">
        <h2
          class="text-xs font-semibold tracking-wider uppercase text-n-slate-10"
        >
          {{ t('WORKFORCE_OVERVIEW_REPORTS.VOLUME_SPLIT.HEADER') }}
        </h2>
        <div
          class="grid grid-cols-1 border lg:grid-cols-5 rounded-2xl border-n-weak bg-n-solid-1"
        >
          <div class="p-6 lg:col-span-3">
            <div class="h-56">
              <BarChart
                :collection="volumeSplitChartData"
                :chart-options="horizontalChartOptions"
              />
            </div>
            <div
              v-if="aiTrend || humanTrend"
              class="flex flex-wrap items-center mt-4 text-xs gap-x-5 gap-y-1 text-n-slate-11"
            >
              <span class="text-n-slate-10">
                {{ t('AGENT_PERFORMANCE_REPORTS.VS_LAST_PERIOD') }}
              </span>
              <span v-if="aiTrend" class="flex items-center gap-1.5">
                {{ t('WORKFORCE_OVERVIEW_REPORTS.AI_VS_HUMAN.AI_LABEL') }}
                <span
                  class="rounded-full px-1.5 py-0.5 font-medium"
                  :class="aiTrend.chipClass"
                >
                  {{ aiTrend.label }}
                </span>
              </span>
              <span v-if="humanTrend" class="flex items-center gap-1.5">
                {{ t('WORKFORCE_OVERVIEW_REPORTS.AI_VS_HUMAN.HUMAN_LABEL') }}
                <span
                  class="rounded-full px-1.5 py-0.5 font-medium"
                  :class="humanTrend.chipClass"
                >
                  {{ humanTrend.label }}
                </span>
              </span>
            </div>
          </div>
          <div
            class="flex flex-col justify-center border-t divide-y lg:border-t-0 lg:border-s divide-n-weak border-n-weak lg:col-span-2"
          >
            <div
              v-for="row in volumeRows"
              :key="row.key"
              class="flex items-center justify-between gap-3 px-6 py-4"
            >
              <span class="flex items-center gap-2.5 text-sm text-n-slate-11">
                <span
                  v-if="row.dot"
                  class="rounded-full size-2"
                  :style="{ backgroundColor: row.dot }"
                />
                <span v-else class="rounded-full size-2 bg-n-slate-6" />
                {{ row.label }}
              </span>
              <span class="text-lg font-semibold text-n-slate-12">
                {{ row.value }}
              </span>
            </div>
          </div>
        </div>
      </section>

      <!-- Agent performance cards, ranked busiest-first -->
      <section class="flex flex-col gap-3">
        <div class="flex items-center justify-between">
          <h2
            class="text-xs font-semibold tracking-wider uppercase text-n-slate-10"
          >
            {{ t('WORKFORCE_OVERVIEW_REPORTS.AGENT_PERFORMANCE.HEADER') }}
          </h2>
          <div class="flex items-center gap-2">
            <span
              v-if="teamResolvedTotal"
              class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-slate-3 text-n-slate-11"
            >
              {{
                t('TEAM_PERFORMANCE_REPORTS.AGENT_CARDS.TEAM_RESOLVED', {
                  count: teamResolvedTotal,
                })
              }}
            </span>
            <span
              v-if="teamCsat"
              class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-slate-3 text-n-slate-11"
            >
              {{
                t('TEAM_PERFORMANCE_REPORTS.AGENT_CARDS.TEAM_CSAT', {
                  rating: teamCsat,
                })
              }}
            </span>
            <span
              v-if="human.agent_count"
              class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-slate-3 text-n-slate-11"
            >
              {{
                t('WORKFORCE_OVERVIEW_REPORTS.AGENT_PERFORMANCE.AGENT_COUNT', {
                  count: human.agent_count,
                })
              }}
            </span>
          </div>
        </div>
        <p v-if="!agents.length" class="text-sm text-n-slate-11">
          {{ t('WORKFORCE_OVERVIEW_REPORTS.AGENT_PERFORMANCE.EMPTY') }}
        </p>
        <div
          v-else
          class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
        >
          <article
            v-for="(agent, index) in rankedAgents"
            :key="agent.id"
            class="flex flex-col gap-5 p-6 border rounded-2xl border-n-weak bg-n-solid-1"
            :class="{ 'opacity-50': !hasAgentActivity(agent) }"
          >
            <!-- Identity + rank -->
            <div class="flex items-center gap-3">
              <span
                class="flex items-center justify-center flex-shrink-0 text-sm font-semibold text-white rounded-full size-10"
                :style="{ backgroundColor: agentColorMap.get(agent.id) }"
              >
                {{ agentInitials(agent.name) }}
              </span>
              <div class="min-w-0">
                <h3 class="text-sm font-semibold truncate text-n-slate-12">
                  {{ agent.name }}
                </h3>
                <span
                  v-if="hasAgentActivity(agent)"
                  class="text-xs text-n-slate-10"
                >
                  {{
                    t('TEAM_PERFORMANCE_REPORTS.AGENT_CARDS.RANK', {
                      rank: index + 1,
                    })
                  }}
                </span>
                <span v-else class="text-xs text-n-slate-10">
                  {{ t('TEAM_PERFORMANCE_REPORTS.AGENT_CARDS.NO_ACTIVITY') }}
                </span>
              </div>
            </div>

            <!-- Share of team conversations -->
            <div class="flex flex-col gap-1.5">
              <div class="flex items-center justify-between text-xs">
                <span class="text-n-slate-11">
                  {{ t('TEAM_PERFORMANCE_REPORTS.AGENT_CARDS.SHARE') }}
                </span>
                <span class="font-medium text-n-slate-12">
                  {{ `${agentShare(agent)}%` }}
                </span>
              </div>
              <div
                class="w-full h-1.5 overflow-hidden rounded-full"
                :style="{ backgroundColor: shareTrackColor(agent) }"
              >
                <div
                  class="h-full rounded-full"
                  :style="{
                    width: `${agentShare(agent)}%`,
                    backgroundColor: agentColorMap.get(agent.id),
                  }"
                />
              </div>
            </div>

            <!-- Volume metrics: fixed three-line anatomy (label / value /
                 trend) with the trend line always reserved, so the big
                 figures align across all three columns -->
            <div class="grid grid-cols-3 gap-4">
              <div v-for="metric in volumeMetrics" :key="metric.key">
                <span class="block text-xs text-n-slate-11">
                  {{ metric.label }}
                </span>
                <div class="flex items-center gap-1.5 mt-1">
                  <span class="text-2xl font-semibold text-n-slate-12">
                    {{ metric.format(agent[metric.key]?.current) }}
                  </span>
                  <span
                    v-if="leaders[metric.key] === agent.id"
                    v-tooltip="t('AGENT_PERFORMANCE_REPORTS.TOP_AGENT')"
                    class="flex-shrink-0 i-lucide-trophy size-3.5 text-n-amber-10"
                  />
                </div>
                <div class="h-4 mt-0.5">
                  <span
                    v-if="volumeTrend(agent, metric)"
                    v-tooltip="t('AGENT_PERFORMANCE_REPORTS.VS_LAST_PERIOD')"
                    class="text-xs font-medium"
                    :class="volumeTrend(agent, metric).textClass"
                  >
                    {{ volumeTrend(agent, metric).label }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Outcomes + speed: label-value rows so long values never wrap -->
            <div class="flex flex-col gap-2.5 pt-4 border-t border-n-weak">
              <div class="flex items-center justify-between gap-3">
                <span class="text-xs text-n-slate-11">
                  {{ t('TEAM_PERFORMANCE_REPORTS.AGENT_CARDS.RESOLVED') }}
                </span>
                <div class="flex items-center gap-1.5">
                  <span
                    v-if="leaders.conversations_resolved === agent.id"
                    v-tooltip="t('AGENT_PERFORMANCE_REPORTS.TOP_AGENT')"
                    class="flex-shrink-0 i-lucide-trophy size-3.5 text-n-amber-10"
                  />
                  <span
                    class="text-sm font-semibold whitespace-nowrap tabular-nums text-n-slate-12"
                  >
                    {{ agent.conversations_resolved?.current ?? 0 }}
                  </span>
                  <span
                    v-if="resolvedTrend(agent)"
                    v-tooltip="t('AGENT_PERFORMANCE_REPORTS.VS_LAST_PERIOD')"
                    class="rounded-full px-1.5 py-0.5 text-xs font-medium"
                    :class="resolvedTrend(agent).chipClass"
                  >
                    {{ resolvedTrend(agent).label }}
                  </span>
                </div>
              </div>
              <div class="flex items-center justify-between gap-3">
                <span class="text-xs text-n-slate-11">
                  {{ t('TEAM_PERFORMANCE_REPORTS.AGENT_CARDS.CSAT') }}
                </span>
                <div class="flex items-center gap-1.5">
                  <span
                    v-if="leaders.csat_rating === agent.id"
                    v-tooltip="t('AGENT_PERFORMANCE_REPORTS.TOP_AGENT')"
                    class="flex-shrink-0 i-lucide-trophy size-3.5 text-n-amber-10"
                  />
                  <span
                    class="text-sm font-semibold whitespace-nowrap tabular-nums text-n-slate-12"
                  >
                    {{ formatCsatRating(agent.csat_rating?.current) }}
                  </span>
                  <span
                    v-if="agent.csat_responses"
                    class="text-xs text-n-slate-10"
                  >
                    {{ `(${agent.csat_responses})` }}
                  </span>
                </div>
              </div>
              <div
                v-for="metric in timeMetrics"
                :key="metric.key"
                class="flex items-center justify-between gap-3"
              >
                <span class="text-xs text-n-slate-11">
                  {{ metric.label }}
                </span>
                <div class="flex items-center gap-1.5">
                  <span
                    v-if="leaders[metric.key] === agent.id"
                    v-tooltip="t('AGENT_PERFORMANCE_REPORTS.TOP_AGENT')"
                    class="flex-shrink-0 i-lucide-trophy size-3.5 text-n-amber-10"
                  />
                  <span
                    class="text-sm font-semibold whitespace-nowrap tabular-nums text-n-slate-12"
                  >
                    {{ metric.format(agent[metric.key]?.current) }}
                  </span>
                  <span
                    v-if="packedTrend(agent[metric.key], metric.direction)"
                    class="rounded-full px-1.5 py-0.5 text-xs font-medium"
                    :class="
                      packedTrend(agent[metric.key], metric.direction).chipClass
                    "
                  >
                    {{ packedTrend(agent[metric.key], metric.direction).label }}
                  </span>
                </div>
              </div>
              <div class="flex items-center justify-between gap-3">
                <span class="text-xs text-n-slate-11">
                  {{ t('AGENT_PERFORMANCE_REPORTS.TABLE.AVG_CALL_DURATION') }}
                </span>
                <span
                  class="text-sm font-semibold whitespace-nowrap tabular-nums text-n-slate-12"
                >
                  {{ formatCompactDuration(agent.avg_call_duration) }}
                </span>
              </div>
            </div>
          </article>
        </div>
      </section>

      <!-- Funnel: inline stats + chart in one card -->
      <section class="flex flex-col gap-3">
        <h2
          class="text-xs font-semibold tracking-wider uppercase text-n-slate-10"
        >
          {{ t('WORKFORCE_OVERVIEW_REPORTS.FUNNEL.HEADER') }}
        </h2>
        <div class="border rounded-2xl border-n-weak bg-n-solid-1">
          <div class="grid grid-cols-3 divide-x divide-n-weak">
            <div
              v-for="stat in funnelStats"
              :key="stat.key"
              class="flex flex-col gap-1 p-6"
            >
              <span class="text-sm font-medium text-n-slate-11">
                {{ stat.label }}
              </span>
              <span class="text-3xl font-semibold text-n-slate-12">
                {{ stat.value }}
              </span>
            </div>
          </div>
          <div class="p-6 border-t border-n-weak">
            <h3 class="mb-4 text-sm font-medium text-n-slate-12">
              {{ t('WORKFORCE_OVERVIEW_REPORTS.FUNNEL.BY_SERVICE') }}
            </h3>
            <div class="h-56">
              <BarChart
                :collection="funnelChartData"
                :chart-options="horizontalChartOptions"
              />
            </div>
          </div>
        </div>
      </section>

      <!-- Escalations + risk signals, one quiet strip -->
      <section class="flex flex-col gap-3">
        <h2
          class="text-xs font-semibold tracking-wider uppercase text-n-slate-10"
        >
          {{
            `${t('WORKFORCE_OVERVIEW_REPORTS.ESCALATIONS.HEADER')} · ${t(
              'WORKFORCE_OVERVIEW_REPORTS.RISK.HEADER'
            )}`
          }}
        </h2>
        <div
          class="grid grid-cols-2 border divide-y sm:grid-cols-3 lg:grid-cols-6 lg:divide-y-0 lg:divide-x divide-n-weak rounded-2xl border-n-weak bg-n-solid-1"
        >
          <div
            v-for="stat in attentionStats"
            :key="stat.key"
            class="flex flex-col gap-1 p-5"
          >
            <span class="text-xs font-medium text-n-slate-11">
              {{ stat.label }}
            </span>
            <span
              class="text-2xl font-semibold"
              :class="stat.value ? 'text-n-slate-12' : 'text-n-slate-9'"
            >
              {{ stat.value }}
            </span>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>
